import {
  IconAdjustments,
  IconArchive,
  IconArrowsExchange,
  IconCalendarDollar,
  IconBuilding,
  IconCategory,
  IconChecklist,
  IconClockDollar,
  IconContract,
  IconDashboard,
  IconFileInvoice,
  IconFiles,
  IconFlag,
  IconInbox,
  IconKey,
  IconHistory,
  IconReceipt,
  IconReceiptTax,
  IconReportAnalytics,
  IconShieldLock,
  IconShoppingCart,
  IconTags,
  IconUsers,
  type IconProps,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { PERMISOS, VER_SOLICITUDES } from "@/lib/permisos";

/**
 * Declaración de los items de navegación del AppShell. Cada uno declara los
 * PERMISOS que lo hacen visible (RBAC · Ruta B) · si no hay `permisos`, lo ven
 * todos. La filtración se hace en `filtrarNavParaPermisos` (helper puro · testeable).
 */
export interface NavItem {
  /**
   * Etiqueta del item · puede depender de los roles del usuario (ej. Mesa de
   * Control ve TODAS las solicitudes, no solo "las suyas" · decisión P29). La
   * etiqueta sigue siendo por ROL (es texto de display, no control de acceso).
   */
  label: string | ((roles: ReadonlyArray<string>) => string);
  href: string;
  icon: ComponentType<IconProps>;
  /** Permisos que hacen visible el item (basta tener CUALQUIERA). */
  permisos?: ReadonlyArray<string>;
}

/** Resuelve la etiqueta final de un item para los roles del usuario actual. */
export function etiquetaNavItem(item: NavItem, roles: ReadonlyArray<string>): string {
  return typeof item.label === "function" ? item.label(roles) : item.label;
}

export const NAV_PRINCIPAL: ReadonlyArray<NavItem> = [
  { label: "Dashboard", href: "/dashboard", icon: IconDashboard },
  {
    // Mesa de Control ve TODAS las solicitudes del sistema, no solo "las suyas"
    // (decisión P29) · mismo espejo que el título de `MisSolicitudesPage`.
    label: (roles) =>
      roles.includes("MesaDeControl") ? "Solicitudes de pago" : "Mis solicitudes de pago",
    href: "/solicitudes",
    icon: IconFileInvoice,
    permisos: VER_SOLICITUDES,
  },
  {
    label: "Mis facturas",
    href: "/facturas",
    icon: IconReceipt,
    permisos: [PERMISOS.facturasVer],
  },
  {
    label: "Bandeja de autorización",
    href: "/autorizacion/bandeja",
    icon: IconChecklist,
    permisos: [PERMISOS.autorizacionAutorizar],
  },
  {
    // P44 · FB60 Contrapago · solicitudes Autorizadas en espera del CFDI
    // real (llega después del pago). Solicitante ve las suyas, Mesa de
    // Control ve todas (mismo criterio que el resto de bandejas P29).
    label: "Contrapagos pendientes",
    href: "/contrapagos-pendientes",
    icon: IconClockDollar,
    permisos: [PERMISOS.contrapagosGestionar],
  },
  {
    // Calendario de pagos (P39) · pagos programados · EXCLUSIVO Mesa de Control.
    label: "Calendario de pagos",
    href: "/calendario-pagos",
    icon: IconCalendarDollar,
    permisos: [PERMISOS.calendarioPagosVer],
  },
  {
    // Histórico Documental (P37) · acervo transversal · EXCLUSIVO Mesa de Control.
    // El Solicitante NO tiene acceso (RN-03 · ve sus documentos en el wizard).
    label: "Histórico documental",
    href: "/historico-documental",
    icon: IconArchive,
    permisos: [PERMISOS.historicoDocumentalVer],
  },
  {
    // Reportería (módulo de reporteo dinámico · S11). El acceso real lo decide
    // el backend por configuración; se ofrece a los roles que reportan y la
    // página muestra "sin acceso" a quien no tenga configuración asignada.
    label: "Reportería",
    href: "/reporteria",
    icon: IconReportAnalytics,
    permisos: [PERMISOS.reporteriaUsar],
  },
];

/**
 * Href del item con badge de solicitudes de catálogo pendientes (P38) ·
 * `AppShellNavbar` le monta el contador consultado por polling.
 */
export const HREF_SOLICITUDES_CATALOGO = "/admin/solicitudes-catalogo";

/** Un grupo (subcategoría) del menú de administración · agrupa items afines. */
export interface NavGrupo {
  titulo: string;
  items: ReadonlyArray<NavItem>;
}

/**
 * Menú de administración agrupado en subcategorías lógicas. El sidebar
 * renderiza cada grupo con su sub-encabezado y oculta los grupos vacíos (cuando
 * el usuario no ve ninguno de sus items · ej. un Auditor solo ve "Auditoría").
 * Orden dentro de cada grupo: por afinidad · mantenerlo al agregar items.
 */
export const NAV_ADMIN_GRUPOS: ReadonlyArray<NavGrupo> = [
  {
    titulo: "Organización y accesos",
    items: [
      { label: "Organizaciones", href: "/admin/organizaciones", icon: IconBuilding, permisos: [PERMISOS.adminOrganizaciones] },
      { label: "Áreas", href: "/admin/areas", icon: IconCategory, permisos: [PERMISOS.adminAreas] },
      { label: "Usuarios", href: "/admin/usuarios", icon: IconUsers, permisos: [PERMISOS.adminUsuarios] },
      { label: "Roles", href: "/admin/roles", icon: IconShieldLock, permisos: [PERMISOS.adminRoles] },
      { label: "Permisos", href: "/admin/permisos", icon: IconKey, permisos: [PERMISOS.adminPermisos] },
      { label: "Flujos de autorización", href: "/admin/flujos-autorizacion", icon: IconFlag, permisos: [PERMISOS.adminFlujosAutorizacion] },
    ],
  },
  {
    titulo: "Catálogos",
    items: [
      { label: "Proveedores", href: "/admin/proveedores", icon: IconBuilding, permisos: [PERMISOS.adminProveedores] },
      { label: "Catálogos contables", href: "/admin/catalogos-contables", icon: IconCategory, permisos: [PERMISOS.adminCatalogosContables] },
      { label: "Catálogo Prod/Serv SAT", href: "/admin/catalogos-sat", icon: IconCategory, permisos: [PERMISOS.adminCatalogosSat] },
      { label: "Cuentas por clave SAT", href: "/admin/mapeos-clave-cuenta", icon: IconTags, permisos: [PERMISOS.adminMapeosClaveCuenta] },
      { label: "Indicadores de impuesto", href: "/admin/indicadores-impuesto", icon: IconReceiptTax, permisos: [PERMISOS.adminIndicadoresImpuesto] },
      { label: "Equivalencias de unidad", href: "/admin/equivalencias-unidad", icon: IconArrowsExchange, permisos: [PERMISOS.adminEquivalenciasUnidad] },
      { label: "Tipos de documento", href: "/admin/tipos-documento", icon: IconFiles, permisos: [PERMISOS.adminTiposDocumento] },
    ],
  },
  {
    titulo: "Documentos de compra (SAP)",
    items: [
      { label: "Órdenes de compra", href: "/admin/ordenes-compra", icon: IconShoppingCart, permisos: [PERMISOS.adminComprasSap] },
      { label: "Contratos anualizados", href: "/admin/contratos-anualizados", icon: IconContract, permisos: [PERMISOS.adminComprasSap] },
    ],
  },
  {
    titulo: "Sistema",
    items: [
      // Bandeja de peticiones de alta de elementos de catálogo (P38).
      { label: "Solicitudes de catálogos", href: HREF_SOLICITUDES_CATALOGO, icon: IconInbox, permisos: [PERMISOS.adminSolicitudesCatalogo] },
      { label: "Reportería", href: "/admin/reporteria", icon: IconReportAnalytics, permisos: [PERMISOS.reporteriaAdministrar] },
      { label: "Configuraciones", href: "/admin/configuraciones", icon: IconAdjustments, permisos: [PERMISOS.adminConfiguraciones] },
      { label: "Auditoría", href: "/admin/auditoria", icon: IconHistory, permisos: [PERMISOS.auditoriaVer] },
    ],
  },
];

/** Lista plana de TODOS los items de administración (deriva de los grupos). */
export const NAV_ADMIN: ReadonlyArray<NavItem> = NAV_ADMIN_GRUPOS.flatMap((g) => g.items);

/**
 * Filtra una lista de items a los que el usuario actual puede ver según sus
 * PERMISOS. Items sin `permisos` son visibles para todos.
 */
export function filtrarNavParaPermisos(
  items: ReadonlyArray<NavItem>,
  permisos: ReadonlyArray<string>
): NavItem[] {
  return items.filter((i) => !i.permisos || i.permisos.some((p) => permisos.includes(p)));
}
